import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Rating,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useBookingCopyTranslation as useTranslation } from '../../i18n/bookingCopy';

interface TutorReviewDialogProps {
  open: boolean;
  tutorName: string;
  canSubmit: boolean;
  rating: number;
  comment: string;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onRatingChange: (rating: number) => void;
  onCommentChange: (comment: string) => void;
  onSubmit: () => void;
}

export default function TutorReviewDialog({
  open,
  tutorName,
  canSubmit,
  rating,
  comment,
  submitting,
  error,
  onClose,
  onRatingChange,
  onCommentChange,
  onSubmit,
}: TutorReviewDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" noWrap sx={{ flex: 1, minWidth: 0, pr: 1 }}>
          {tutorName}
        </Typography>
        <IconButton aria-label={t('common.close', 'Close')} onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2.5 }}>
        {canSubmit ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Typography variant="body1" color="text.secondary">
                {t('review.rateYourExperience', 'Rate your experience')}
              </Typography>
              <Rating
                value={rating}
                onChange={(_, newValue) => onRatingChange(newValue ?? 0)}
                size="large"
              />
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label={t('review.comment', 'Share your experience (optional)')}
              placeholder={t(
                'review.tutorCommentPlaceholder',
                'Tell others about your experience with this tutor...',
              )}
              value={comment}
              onChange={(e) => onCommentChange(e.target.value)}
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary">
            {t(
              'review.tutorBookingNotEligible',
              'Only confirmed tutor sessions can be reviewed.',
            )}
          </Typography>
        )}
      </DialogContent>

      {canSubmit && (
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} variant="outlined">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={onSubmit}
            variant="contained"
            disabled={submitting || rating === 0}
          >
            {submitting
              ? t('review.submitting', 'Submitting...')
              : t('review.submit', 'Submit Review')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
